import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import {
  Download, FileText, FileSpreadsheet, Mail, Truck,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, subMonths, addMonths } from 'date-fns';

import {
  COLORS, CHART_COLORS, fmtR,
  RPT_AXIS_TICK, RPT_TOOLTIP_STYLE, RPT_BAR_RADIUS_TOP,
  buildMonthOptions,
} from '../components/reports/constants';
import { ReportsFilterBar } from '../components/reports/ReportsFilterBar';
import { VehicleCostBreakdown } from '../components/reports/VehicleCostBreakdown';
import { FuelEfficiencyCard } from '../components/reports/FuelEfficiencyCard';
import { IncidentsByDriverCard } from '../components/reports/IncidentsByDriverCard';
import { DataQualityCard } from '../components/reports/DataQualityCard';
import { ServiceBudgetCard } from '../components/reports/ServiceBudgetCard';

const ReportsPage = () => {
  const [vehicles, setVehicles] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [incidentReport, setIncidentReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [vehicleFilter, setVehicleFilter] = useState('all');
  const [costTypeFilter, setCostTypeFilter] = useState('all');
  const [exporting, setExporting] = useState(false);
  const [expandedVehicle, setExpandedVehicle] = useState(null);
  const [vehicleTxns, setVehicleTxns] = useState([]);
  const [loadingTxns, setLoadingTxns] = useState(false);
  const [emailReportEnabled, setEmailReportEnabled] = useState(false);
  const [emailReportLoading, setEmailReportLoading] = useState(false);
  // Persist the "exclude private vehicles" preference so managers don't have
  // to toggle it every visit.
  const [excludePrivate, setExcludePrivate] = useState(
    () => localStorage.getItem('reports.excludePrivate') === '1'
  );
  useEffect(() => {
    localStorage.setItem('reports.excludePrivate', excludePrivate ? '1' : '0');
  }, [excludePrivate]);

  const monthOptions = buildMonthOptions();

  const getMonthRange = useCallback(() => {
    const [y, m] = selectedMonth.split('-').map(Number);
    const d = new Date(y, m - 1, 1);
    return {
      start: format(startOfMonth(d), 'yyyy-MM-dd'),
      end: format(endOfMonth(d), 'yyyy-MM-dd'),
    };
  }, [selectedMonth]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { start, end } = getMonthRange();
      const [vehiclesData, reportSummary, incidentReport] = await Promise.all([
        api.getVehicles(),
        api.getReportSummary({
          start_date: start,
          end_date: end,
          vehicle_id: vehicleFilter !== 'all' ? vehicleFilter : undefined,
          cost_type: costTypeFilter !== 'all' ? costTypeFilter : undefined,
          group_by: 'month',
        }),
        api.getIncidentsByDriver(start, end).catch(() => ({ by_driver: [], by_month: [], total_incidents: 0 })),
      ]);
      setVehicles(vehiclesData);
      setReportData(reportSummary);
      setIncidentReport(incidentReport);
    } catch {
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  }, [getMonthRange, vehicleFilter, costTypeFilter]);

  useEffect(() => { fetchData(); setExpandedVehicle(null); setVehicleTxns([]); }, [fetchData]);

  // Fetch email report settings
  useEffect(() => {
    const fetchEmailSetting = async () => {
      try {
        const data = await api.getEmailReportSettings();
        setEmailReportEnabled(data.enabled);
      } catch (error) { console.error('Failed to load email settings:', error); }
    };
    fetchEmailSetting();
  }, []);

  const toggleEmailReport = async () => {
    setEmailReportLoading(true);
    try {
      const result = await api.updateEmailReportSettings(!emailReportEnabled);
      setEmailReportEnabled(result.enabled);
      toast.success(result.message);
    } catch {
      toast.error('Failed to update email report settings');
    } finally {
      setEmailReportLoading(false);
    }
  };

  const handleExportVehiclesPDF = async () => {
    setExporting(true);
    try {
      const blob = await api.exportVehiclesPDF();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `vehicle_register_${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Vehicle register PDF exported');
    } catch { toast.error('Failed to export'); }
    finally { setExporting(false); }
  };

  const handleExportVehiclesExcel = async () => {
    setExporting(true);
    try {
      const blob = await api.exportVehiclesExcel();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `vehicle_register_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Vehicle register Excel exported');
    } catch { toast.error('Failed to export'); }
    finally { setExporting(false); }
  };

  const toggleVehicleTxns = async (vehicleId) => {
    if (expandedVehicle === vehicleId) {
      setExpandedVehicle(null);
      setVehicleTxns([]);
      return;
    }
    setExpandedVehicle(vehicleId);
    setLoadingTxns(true);
    try {
      const { start, end } = getMonthRange();
      const costs = await api.getCosts({ vehicle_id: vehicleId, start_date: start, end_date: end });
      setVehicleTxns(costs);
    } catch {
      toast.error('Failed to load transactions');
      setVehicleTxns([]);
    } finally {
      setLoadingTxns(false);
    }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const { start, end } = getMonthRange();
      const blob = await api.exportPDF({
        start_date: start, end_date: end,
        vehicle_id: vehicleFilter !== 'all' ? vehicleFilter : undefined,
        cost_type: costTypeFilter !== 'all' ? costTypeFilter : undefined,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fleet_report_${selectedMonth}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF exported');
    } catch { toast.error('Failed to export PDF'); }
    finally { setExporting(false); }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const { start, end } = getMonthRange();
      const blob = await api.exportExcel({
        start_date: start, end_date: end,
        vehicle_id: vehicleFilter !== 'all' ? vehicleFilter : undefined,
        cost_type: costTypeFilter !== 'all' ? costTypeFilter : undefined,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fleet_report_${selectedMonth}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel exported');
    } catch { toast.error('Failed to export Excel'); }
    finally { setExporting(false); }
  };

  // Navigate months
  const prevMonth = () => {
    const [y, m] = selectedMonth.split('-').map(Number);
    const d = subMonths(new Date(y, m - 1, 1), 1);
    setSelectedMonth(format(d, 'yyyy-MM'));
  };
  const nextMonth = () => {
    const [y, m] = selectedMonth.split('-').map(Number);
    const next = addMonths(new Date(y, m - 1, 1), 1);
    if (next <= new Date()) setSelectedMonth(format(next, 'yyyy-MM'));
  };

  const pieData = useMemo(() => reportData?.type_breakdown?.filter(item => item.amount > 0) || [], [reportData]);
  const vbAll = useMemo(() => reportData?.vehicle_breakdown || [], [reportData]);
  // "Exclude private vehicles" toggle — when on, drop every vehicle flagged
  // as include_in_fleet_avg=false from the breakdown table, fuel-efficiency
  // chart, and the top-of-page summary stats.
  const vb = useMemo(
    () => excludePrivate ? vbAll.filter(v => v.include_in_fleet_avg !== false) : vbAll,
    [vbAll, excludePrivate]
  );
  const excludedCount = useMemo(() => vbAll.length - vb.length, [vbAll.length, vb.length]);
  // Total Cost / Fuel cards recompute from the filtered set so managers see
  // true company-fleet numbers when private vehicles are excluded.
  const filteredTotal = useMemo(() => vb.reduce((s, v) => s + (v.total || 0), 0), [vb]);
  const filteredFuel = useMemo(() => vb.reduce((s, v) => s + (v.fuel || 0), 0), [vb]);
  // Only vehicles marked as included contribute to the fleet km/L average (private vehicles excluded)
  const vbForFleetAvg = useMemo(() => vb.filter(v => v.include_in_fleet_avg !== false), [vb]);
  // Split totals by meter type — km-metered vehicles contribute to km/L; forklifts contribute to L/hr.
  const vbKm = useMemo(() => vbForFleetAvg.filter(v => v.meter_type !== 'hours'), [vbForFleetAvg]);
  const vbHrs = useMemo(() => vbForFleetAvg.filter(v => v.meter_type === 'hours'), [vbForFleetAvg]);
  const fleetKm = useMemo(() => vbKm.reduce((s, v) => s + (v.total_km || 0), 0), [vbKm]);
  const fleetHrs = useMemo(() => vbHrs.reduce((s, v) => s + (v.total_hours || 0), 0), [vbHrs]);
  const fleetLKm = useMemo(() => vbKm.reduce((s, v) => s + (v.liters || 0), 0), [vbKm]);
  const fleetLHrs = useMemo(() => vbHrs.reduce((s, v) => s + (v.liters || 0), 0), [vbHrs]);
  const fleetL = fleetLKm + fleetLHrs;
  const fleetAvgKml = fleetKm > 0 && fleetLKm > 0 ? (fleetKm / fleetLKm) : null;
  const fleetAvgLph = fleetHrs > 0 && fleetLHrs > 0 ? (fleetLHrs / fleetHrs) : null;
  const fleetFuel = useMemo(() => vbForFleetAvg.reduce((s, v) => s + (v.fuel || 0), 0), [vbForFleetAvg]);
  const fleetCostPerKm = fleetKm > 0 ? (fleetFuel / fleetKm) : null;

  const fuelEfficiencyData = useMemo(
    () => vb.filter(v => v.km_per_liter).sort((a, b) => (b.km_per_liter || 0) - (a.km_per_liter || 0)),
    [vb]
  );

  const monthLabel = monthOptions.find(m => m.value === selectedMonth)?.label || selectedMonth;

  if (loading && !reportData) {
    return (
      <div className="p-6 lg:p-8 space-y-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48" />
          <div className="grid grid-cols-2 gap-6">
            <div className="h-64 bg-slate-200 rounded-md" />
            <div className="h-64 bg-slate-200 rounded-md" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-heading font-bold text-slate-900 tracking-tight">
            Monthly Report
          </h1>
          <p className="text-slate-500 mt-1">Fleet costs and fuel efficiency for {monthLabel}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={emailReportEnabled ? "default" : "outline"}
            onClick={toggleEmailReport}
            disabled={emailReportLoading}
            className={emailReportEnabled ? "bg-green-600 hover:bg-green-700 text-white" : ""}
            data-testid="email-report-toggle"
          >
            <Mail className="w-4 h-4 mr-2" />
            {emailReportEnabled ? 'Daily Email On' : 'Daily Email Off'}
          </Button>
          <Button variant="outline" onClick={handleExportPDF} disabled={exporting} data-testid="export-pdf-btn">
            <FileText className="w-4 h-4 mr-2" />PDF
          </Button>
          <Button variant="outline" onClick={handleExportExcel} disabled={exporting} data-testid="export-excel-btn">
            <FileSpreadsheet className="w-4 h-4 mr-2" />Excel
          </Button>
          <span className="w-px h-6 bg-slate-300" />
          <Button variant="outline" onClick={handleExportVehiclesPDF} disabled={exporting} data-testid="export-vehicles-pdf-btn">
            <Truck className="w-4 h-4 mr-2" />Vehicles PDF
          </Button>
          <Button variant="outline" onClick={handleExportVehiclesExcel} disabled={exporting} data-testid="export-vehicles-excel-btn">
            <Truck className="w-4 h-4 mr-2" />Vehicles Excel
          </Button>
        </div>
      </div>

      {/* Month Picker + Filters */}
      <ReportsFilterBar
        selectedMonth={selectedMonth}
        setSelectedMonth={setSelectedMonth}
        monthOptions={monthOptions}
        prevMonth={prevMonth}
        nextMonth={nextMonth}
        vehicleFilter={vehicleFilter}
        setVehicleFilter={setVehicleFilter}
        vehicles={vehicles}
        costTypeFilter={costTypeFilter}
        setCostTypeFilter={setCostTypeFilter}
      />

      {/* Exclude Private Vehicles Toggle */}
      <div className="flex items-center justify-between gap-4 px-3 py-2 rounded-md border border-slate-200 bg-slate-50">
        <div className="flex items-center gap-3">
          <label htmlFor="exclude-private" className="flex items-center gap-2 cursor-pointer">
            <input
              id="exclude-private"
              type="checkbox"
              checked={excludePrivate}
              onChange={(e) => setExcludePrivate(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
              data-testid="exclude-private-toggle"
            />
            <span className="text-sm font-medium text-slate-800">Exclude private vehicles</span>
          </label>
          <span className="text-xs text-slate-500 hidden sm:inline">
            Hide vehicles flagged &ldquo;not in fleet average&rdquo; from the totals and breakdown.
          </span>
        </div>
        {excludePrivate && excludedCount > 0 && (
          <span className="text-xs text-slate-600 font-medium" data-testid="excluded-count">
            {excludedCount} vehicle{excludedCount === 1 ? '' : 's'} hidden
          </span>
        )}
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="card-hover">
          <CardContent className="p-4">
            <p className="overline">Total Cost{excludePrivate ? ' (company)' : ''}</p>
            <p className="text-2xl font-heading font-bold text-slate-900 mt-1 data-value" data-testid="total-cost-stat">
              {fmtR(excludePrivate ? filteredTotal : reportData?.total)}
            </p>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4">
            <p className="overline">Entries</p>
            <p className="text-2xl font-heading font-bold text-slate-900 mt-1 data-value">
              {reportData?.entry_count || 0}
            </p>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4">
            <p className="overline">Fuel{excludePrivate ? ' (company)' : ''}</p>
            <p className="text-2xl font-heading font-bold text-orange-500 mt-1 data-value">
              {fmtR(excludePrivate ? filteredFuel : reportData?.type_breakdown?.find(t => t.type === 'Fuel')?.amount)}
            </p>
          </CardContent>
        </Card>
        <Card className={`card-hover ${fleetAvgKml ? (fleetAvgKml >= 10 ? 'border-green-200 bg-green-50/50' : fleetAvgKml >= 7 ? 'border-amber-200 bg-amber-50/50' : 'border-red-200 bg-red-50/50') : ''}`}>
          <CardContent className="p-4">
            <p className="overline">Fleet Avg km/L</p>
            <p className={`text-2xl font-heading font-bold mt-1 data-value ${fleetAvgKml ? (fleetAvgKml >= 10 ? 'text-green-600' : fleetAvgKml >= 7 ? 'text-amber-600' : 'text-red-600') : 'text-slate-400'}`} data-testid="fleet-avg-kml">
              {fleetAvgKml ? `${fleetAvgKml.toFixed(2)} km/L` : '--'}
            </p>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4">
            <p className="overline">Fuel Cost / km</p>
            <p className="text-2xl font-heading font-bold text-blue-500 mt-1 data-value" data-testid="fleet-cost-per-km">
              {fleetCostPerKm ? fmtR(fleetCostPerKm) : '--'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts & Tables */}
      <Tabs defaultValue="vehicles" className="space-y-4">
        <TabsList>
          <TabsTrigger value="vehicles" data-testid="vehicles-tab">By Vehicle</TabsTrigger>
          <TabsTrigger value="trend" data-testid="trend-tab">Cost Trend</TabsTrigger>
          <TabsTrigger value="distribution" data-testid="distribution-tab">Distribution</TabsTrigger>
          <TabsTrigger value="efficiency" data-testid="efficiency-tab">Fuel Efficiency</TabsTrigger>
          <TabsTrigger value="service-budget" data-testid="service-budget-tab">Cost Budget</TabsTrigger>
          <TabsTrigger value="data-quality" data-testid="data-quality-tab">Data Quality</TabsTrigger>
        </TabsList>

        {/* By Vehicle Tab - PRIMARY */}
        <TabsContent value="vehicles">
          <VehicleCostBreakdown
            vb={vb}
            monthLabel={monthLabel}
            expandedVehicle={expandedVehicle}
            toggleVehicleTxns={toggleVehicleTxns}
            vehicleTxns={vehicleTxns}
            loadingTxns={loadingTxns}
            fleetAvgKml={fleetAvgKml}
            fleetAvgLph={fleetAvgLph}
            fleetKm={fleetKm}
            fleetHrs={fleetHrs}
            fleetL={fleetL}
          />
        </TabsContent>

        {/* Cost Trend Tab */}
        <TabsContent value="trend">
          <Card className="card-hover">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-heading font-semibold">Cost Over Time</CardTitle>
            </CardHeader>
            <CardContent>
              {reportData?.period_breakdown?.length > 0 ? (
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={reportData.period_breakdown}>
                    <XAxis dataKey="period" axisLine={false} tickLine={false} tick={RPT_AXIS_TICK} />
                    <YAxis axisLine={false} tickLine={false} tick={RPT_AXIS_TICK} tickFormatter={(v) => `R${v}`} />
                    <Tooltip formatter={(value) => [fmtR(value), '']}
                      contentStyle={RPT_TOOLTIP_STYLE} />
                    <Bar dataKey="fuel" stackId="a" fill={CHART_COLORS.fuel} name="Fuel" />
                    <Bar dataKey="service" stackId="a" fill={CHART_COLORS.service} name="Service" />
                    <Bar dataKey="tyres" stackId="a" fill={CHART_COLORS.tyres} name="Tyres" />
                    <Bar dataKey="labour" stackId="a" fill={CHART_COLORS.labour} name="Labour" />
                    <Bar dataKey="other" stackId="a" fill={CHART_COLORS.other} name="Other" radius={RPT_BAR_RADIUS_TOP} />
                    <Legend />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[350px] flex items-center justify-center text-slate-400">
                  No data for {monthLabel}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Distribution Tab */}
        <TabsContent value="distribution">
          <Card className="card-hover">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-heading font-semibold">Cost Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={350}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={80} outerRadius={130} paddingAngle={2} dataKey="amount" nameKey="type"
                      label={({ type, percent }) => `${type} ${(percent * 100).toFixed(0)}%`}>
                      {pieData.map((entry) => (
                        <Cell key={`cell-${entry.type}`} fill={COLORS[pieData.indexOf(entry) % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => fmtR(value)} contentStyle={RPT_TOOLTIP_STYLE} />
                    <Legend verticalAlign="bottom" iconType="circle" />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[350px] flex items-center justify-center text-slate-400">
                  No data for {monthLabel}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Fuel Efficiency Tab */}
        <TabsContent value="efficiency">
          <FuelEfficiencyCard fuelEfficiencyData={fuelEfficiencyData} monthLabel={monthLabel} />
        </TabsContent>

        {/* Service Budget Tab */}
        <TabsContent value="service-budget">
          <ServiceBudgetCard months={3} />
        </TabsContent>

        {/* Data Quality Tab */}
        <TabsContent value="data-quality">
          <DataQualityCard
            startDate={getMonthRange().start}
            endDate={getMonthRange().end}
            monthLabel={monthLabel}
          />
        </TabsContent>
      </Tabs>

      <IncidentsByDriverCard incidentReport={incidentReport} monthLabel={monthLabel} />
    </div>
  );
};

export default ReportsPage;
